import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'

const INDUSTRIES = [
  'Construction', 'Mining', 'Manufacturing', 'Transport & Logistics', 'Healthcare',
  'Agriculture', 'Utilities', 'Retail', 'Hospitality', 'Education', 'Other',
]

export default function RegisterPage() {
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('token')

  const [inviteDetails, setInviteDetails] = useState<{ email: string; role: string; tenant_name: string; site_name?: string } | null>(null)
  const [inviteError, setInviteError] = useState('')
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken)

  const [form, setForm] = useState({
    tenantName: '', email: '', password: '', firstName: '', lastName: '', industry: '',
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { setAuth } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (!inviteToken) return
    api.get(`/auth/invite-lookup?token=${inviteToken}`)
      .then(res => {
        setInviteDetails(res.data)
        setForm(f => ({ ...f, email: res.data.email }))
      })
      .catch(() => setInviteError('This invitation link is invalid or has expired.'))
      .finally(() => setInviteLoading(false))
  }, [inviteToken])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (form.password.length < 12) { setError('Password must be at least 12 characters'); return }
    setLoading(true)
    try {
      if (inviteToken && inviteDetails) {
        const { data } = await api.post('/auth/accept-invite', {
          token: inviteToken,
          firstName: form.firstName,
          lastName: form.lastName,
          password: form.password,
        })
        setAuth(data.accessToken, data.refreshToken, data.user)
        navigate('/dashboard')
      } else {
        const { data } = await api.post('/auth/self-register', form)
        setAuth(data.accessToken, data.refreshToken, data.user)
        navigate('/setup')
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  if (inviteToken && inviteLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <p style={{ color: 'var(--text-muted)' }}>Checking invitation...</p>
      </div>
    )
  }

  if (inviteToken && inviteError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
        <div className="w-full max-w-md text-center">
          <div className="card space-y-4">
            <div className="text-4xl">⚠️</div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>Invalid Invitation</h1>
            <p style={{ color: 'var(--text-muted)' }}>{inviteError}</p>
            <Link to="/login" className="btn btn-primary w-full justify-center" style={{ display: 'flex' }}>Go to Login</Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 rounded-2xl items-center justify-center text-white font-bold text-xl mb-4" style={{ background: 'var(--accent)' }}>HI</div>
          {inviteDetails ? (
            <>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Set up your account</h1>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                You've been invited to join <strong>{inviteDetails.tenant_name}</strong>
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Start your free trial</h1>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>30 days free — no credit card required</p>
            </>
          )}
        </div>

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-4">
            {!inviteDetails && (
              <div>
                <label>Organisation name</label>
                <input name="tenantName" value={form.tenantName} onChange={handleChange} required placeholder="Acme Construction Pty Ltd" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label>First name</label>
                <input name="firstName" value={form.firstName} onChange={handleChange} required />
              </div>
              <div>
                <label>Last name</label>
                <input name="lastName" value={form.lastName} onChange={handleChange} required />
              </div>
            </div>

            <div>
              <label>Work email</label>
              {inviteDetails ? (
                <input type="email" value={inviteDetails.email} readOnly
                  style={{ background: 'var(--bg)', color: 'var(--text-secondary)', cursor: 'not-allowed' }} />
              ) : (
                <input type="email" name="email" value={form.email} onChange={handleChange} required />
              )}
            </div>

            <div>
              <label>Password (min 12 characters)</label>
              <input type="password" name="password" value={form.password} onChange={handleChange} required minLength={12} />
            </div>

            {!inviteDetails && (
              <div>
                <label>Industry</label>
                <select name="industry" value={form.industry} onChange={handleChange}>
                  <option value="">Select industry...</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            )}

            {inviteDetails && (
              <div className="p-3 rounded-lg text-sm" style={{ background: 'var(--bg)', color: 'var(--text-secondary)' }}>
                Role: <strong>{inviteDetails.role.replace(/_/g, ' ')}</strong>
                {inviteDetails.site_name && <> · Site: <strong>{inviteDetails.site_name}</strong></>}
              </div>
            )}

            {error && (
              <div className="text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                {error}
              </div>
            )}

            <button type="submit" className="btn btn-primary w-full justify-center" disabled={loading}>
              {loading ? 'Creating account...' : inviteDetails ? 'Create Account' : 'Create free account'}
            </button>
          </form>

          <p className="text-center text-sm mt-4" style={{ color: 'var(--text-muted)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--accent)' }} className="font-medium">Sign in</Link>
          </p>
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'var(--text-muted)' }}>
          All data stored in Australia (AWS ap-southeast-2) · Privacy Act 1988 compliant
        </p>
      </div>
    </div>
  )
}
